/*
 *
 * PatientSchedule
 *
 * Connected container that mounts the patient-facing month-grid calendar
 * (`MonthCalendar`) plus the iCal subscribe kebab (`IcalSubscribeMenu`).
 *
 * Data sources:
 *   - `events`         : `patientSchedule` slice (populated by `fetchSessions`)
 *   - `subscribeUrls`  : `patientSchedule` slice (populated by `fetchProfile`,
 *                        which calls `GET /user/:id` so we get the freshly
 *                        regenerated `calendarFeedUrl` /
 *                        `calendarFeedUrlPrivatized` after a reset). The
 *                        Phase 2 design originally suggested reading these
 *                        from `loggedInUser`, but that user object does not
 *                        currently carry them; the slice's own
 *                        `subscribeUrls` is the practical source of truth.
 *
 * Side-effects on mount:
 *   - dispatch `fetchSessions()`            -> populate the month grid
 *   - dispatch `fetchProfile(loggedInUser.id)` -> populate the kebab URLs
 *
 * Reset flow:
 *   - kebab `onReset` calls `deleteCalendarFeedAPI()` directly (there is no
 *     Redux slice / saga that owns the reset path). On success we re-dispatch
 *     `fetchProfile` to pull the freshly regenerated URLs. On failure we
 *     surface a toast so the user knows the reset did not take effect.
 */

import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { createStructuredSelector } from 'reselect';

import IcalSubscribeMenu from 'components/IcalSubscribeMenu';
import MonthCalendar from 'components/MonthCalendar';
import { makeSelectAuth } from 'containers/App/selectors';
import { deleteCalendarFeedAPI } from 'utils/api';

import { fetchProfile, fetchSessions } from './actions';
import {
	makeSelectError,
	makeSelectEvents,
	makeSelectProfileError,
	makeSelectSubscribeUrls,
} from './selectors';

export function PatientSchedule(props) {
	const {
		auth,
		events,
		subscribeUrls,
		fetchError,
		profileError,
		doFetchSessions,
		doFetchProfile,
	} = props;

	const userId = auth && auth.loggedInUser && auth.loggedInUser.id;

	useEffect(() => {
		doFetchSessions();
	}, [doFetchSessions]);

	useEffect(() => {
		if (userId) {
			doFetchProfile(userId);
		}
	}, [userId, doFetchProfile]);

	useEffect(() => {
		if (fetchError) {
			toast.error('Could not load schedule');
		}
	}, [fetchError]);

	useEffect(() => {
		if (profileError) {
			toast.error('Could not load calendar subscription links');
		}
	}, [profileError]);

	const handleReset = useCallback(async () => {
		try {
			await deleteCalendarFeedAPI();
			if (userId) {
				doFetchProfile(userId);
			}
		} catch (err) {
			toast.error('Failed to reset calendar links');
		}
	}, [userId, doFetchProfile]);

	const feedUrl = (subscribeUrls && subscribeUrls.standard) || '';
	const feedUrlPrivatized =
		(subscribeUrls && subscribeUrls.privatized) || '';

	return (
		<MonthCalendar
			events={events || []}
			type="patient"
			headerAction={
				<IcalSubscribeMenu
					feedUrl={feedUrl}
					feedUrlPrivatized={feedUrlPrivatized}
					onReset={handleReset}
				/>
			}
		/>
	);
}

PatientSchedule.propTypes = {
	auth: PropTypes.object,
	events: PropTypes.array,
	subscribeUrls: PropTypes.shape({
		standard: PropTypes.string,
		privatized: PropTypes.string,
	}),
	fetchError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	profileError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	doFetchSessions: PropTypes.func.isRequired,
	doFetchProfile: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
	auth: makeSelectAuth(),
	events: makeSelectEvents(),
	subscribeUrls: makeSelectSubscribeUrls(),
	fetchError: makeSelectError(),
	profileError: makeSelectProfileError(),
});

const mapDispatchToProps = {
	doFetchSessions: fetchSessions,
	doFetchProfile: fetchProfile,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientSchedule);
